import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UIIconsComponent } from '../../ui/icons/icons.component';
import { AssetsApiService } from '../../../core/http/services/assets.service';
import { map } from 'rxjs';
import { UISkeletonFlatHierarchyComponent } from '../../ui/skeletons/flat-hierarchy/skeleton-flat-hierarchy.component';
import { SecurityPolicy } from '@interface/security-policy.interface';
import { DataCopilotApiService } from '../../../core/http/services/data-copilot.service';
import { MarkdownModule } from 'ngx-markdown';
import { LoaderComponent } from '../loader/loader.component';
import { CacheService } from '../../services/cache.service';

@Component({
  selector: 'app-scope-overview-popup',
  standalone: true,
  imports: [CommonModule, UIIconsComponent, UISkeletonFlatHierarchyComponent, MarkdownModule, LoaderComponent],
  templateUrl: './scope-overview-popup.component.html',
  styleUrl: './scope-overview-popup.component.scss'
})
export class ScopeOverviewPopupComponent implements OnInit {
  @Input() selectedScopeId = '';
  @Output() closePopup: EventEmitter<void> = new EventEmitter();
  assetsApiService = inject(AssetsApiService);
  dataCopilotApiService = inject(DataCopilotApiService);
  cacheService = inject(CacheService);
  private cdr = inject(ChangeDetectorRef);
  activeTab = 'DICTIONARY';


  //Scope overview
  overviewData = '';
  isOverViewLoading = false;
  datePipe = inject(DatePipe);

  // Scope Hierarchy
  selectedTables: any[] = [];
  isScopeDetailsLoading: boolean = false;

  // Scope Details
  public joinSecurity: SecurityPolicy['JOIN_SECURITY'] = [];
  datascopeName = '';
  datascopeDescription = '';
  connectionName = '';
  connectionDescription = '';
  dataSourceName = '';
  connectionCreatedDate = '';
  connectionUpdatedDate = '';
  detailsExpanded = true;
  connectionId = '';
  datascopeId = '';
  overviewError: string | null = null;
  joinRulesExpanded = false;


  ngOnInit() {
    this.initializeScopeOverView();
    this.getScopeDetails();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  onClose(): void {
    this.closePopup.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).id === 'overlay') {
      this.onClose();
    }
  }

  public getScopeDetails(): void {
    this.isScopeDetailsLoading = true;
    this.getAssetsInit(this.selectedScopeId, ['TABLE', 'CONNECTION']).subscribe(
      (data: any) => {
        this.datascopeName = data.name;
        this.datascopeDescription = data.description;
        
        const parameters = data.parameters;
        
        this.selectedTables = parameters.TABLES.map((table: any) => ({
          ...table,
          expanded: false,
        }));
        
        const joinSecurity = parameters.SECURITY_POLICY?.JOIN_SECURITY;
        if (joinSecurity) {
          this.joinSecurity = joinSecurity.map((join: any) => ({
            LEFT_TABLE_FQPN: join.LEFT_TABLE_FQPN,
            LEFT_TABLE_COLUMN_FQPN: join.LEFT_TABLE_COLUMN_FQPN,
            RIGHT_TABLE_FQPN: join.RIGHT_TABLE_FQPN,
            RIGHT_TABLE_COLUMN_FQPN: join.RIGHT_TABLE_COLUMN_FQPN,
            JOIN_TYPE: join.JOIN_TYPE,
          }));
        }
        
        const connectionData = parameters.CONNECTIONS[0];
        this.connectionId = connectionData?.id;
        if (connectionData && connectionData.parameters) {
          this.connectionName = connectionData?.name;
          this.dataSourceName = parameters.DATASOURCE;
          this.connectionDescription = connectionData?.description ?? '';
          this.connectionCreatedDate = connectionData.createdAt;
          this.connectionUpdatedDate = connectionData.updatedAt;
        }
        
        this.isScopeDetailsLoading = false;
        this.cdr.markForCheck();
      }
    );
  }

  public getAssets(primaryAssetId: string[], secondaryAssetType: string[]): any {
    return this.assetsApiService.findAssetsByRelationship(primaryAssetId, secondaryAssetType).pipe(
      map((response: any) => response.data.findAssetsByRelationship)
    );
  }

  public getAssetsInit(assetId: string, assetType: string[]): any {
    return this.assetsApiService.getAssetsByRelationshipsById(assetId, assetType).pipe(
      map((response: any) => response[0])  // Adjust according to your API response structure
    );
  }

  public toggleTable(table: any) {
    const tableId = table.id;

    this.selectedTables = this.selectedTables.map((table: any) => {
      if (table.id === tableId) {
        if (!table.expanded) {
          table.loading = true;
          this.getAssets(
            [this.selectedScopeId, tableId],
            ['COLUMN']
          ).subscribe((columns: any) => {
            table.columns = columns.map((column: any) => ({
              ...column,
            }));
            table.expanded = !table.expanded;
            table.loading = false;
            this.cdr.markForCheck();
          });
        } else {
          table.expanded = !table.expanded;
        }
      } else {
        table.expanded = false;
      }
      return table;
    });
  }

    
  private initializeScopeOverView(): void {
    this.isOverViewLoading = true; 
    const cacheOverview = this.cacheService.get(this.selectedScopeId)

    if(cacheOverview){
      this.overviewData = cacheOverview;
      this.isOverViewLoading = false;
      this.cdr.markForCheck();
    } else {
      // Use the dedicated scope overview endpoint
      const aiModel = this.getCurrentAIModel();
      const aiModelAssetId = aiModel?.id;
      
      const passData = {
        datascopeId: this.selectedScopeId,
        aiModelAssetId: aiModelAssetId
      };

      this.dataCopilotApiService.getScopeOverview(passData).subscribe({
        next: (data) => {
          // The response structure: data.data[0] contains the answer asset with MESSAGE parameter
          const answerAsset = data.data.data?.find((item: any) => item.type === 'ANSWER');
          if (!answerAsset || !answerAsset.parameters?.MESSAGE) {
            this.overviewData = `⚠️ We could not generate an overview for the data scope, please try again later!`;
          } else {
            this.overviewData = answerAsset.parameters.MESSAGE;
            this.cacheService.set(this.selectedScopeId, this.overviewData);
          }
          this.isOverViewLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error generating scope overview:', error);
          this.isOverViewLoading = false;
          this.overviewError =
            error.error?.message ||
            'Failed to generate overview. Please try again.';
          this.cdr.markForCheck();
        }
      });
    }
  }

  private getCurrentAIModel(): any {    
    const aiModelStr = localStorage.getItem('AI_MODEL');
    if (aiModelStr) {
      try {
        const parsed = JSON.parse(aiModelStr);
        if (parsed && parsed.id) {
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing AI_MODEL from localStorage:', e);
      }
    }
    
    // Return empty object if nothing is found - should be initialized by app.component
    return {};
  }

  toggleConnectionDetails(): void {
    this.detailsExpanded = !this.detailsExpanded;
  }

  // Toggle the join rules collapsible
  toggleJoinRules(): void {
    this.joinRulesExpanded = !this.joinRulesExpanded;
  }

  resetScopeDetails(): void {
    this.joinSecurity = [];
    this.connectionName = '';
    this.dataSourceName = '';
    this.connectionUpdatedDate = '';
    this.connectionUpdatedDate = '';
   }
}
